import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { PublicFooter } from "@/components/PublicFooter";
import { smsProgram } from "@/lib/sms-program";

/**
 * Privacy policy for the public appointment scheduling site, linked from the SMS opt-in.
 *
 * Scoped to what this site collects and does with it. The mobile information paragraph is the one
 * carrier review looks for by name, and it has to keep saying what it says here.
 */
export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-3xl mx-auto space-y-6 text-slate-700">
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: "#009681" }}>
              Privacy Policy
            </h1>
            <p className="mt-1 text-sm text-slate-500">Last updated: September 10, 2026</p>
          </div>

          <p>
            This policy covers the {smsProgram.organizationName} Energy Assistance appointment website and
            the text messages and calls that go with an appointment booked through it.
          </p>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">What we collect</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Your name, date of birth and Social Security number, used to identify your household</li>
              <li>Your phone number and email address, used to reach you about your appointment</li>
              <li>Your address and ZIP code, used to assign you to a service site</li>
              <li>Your household size and income, used to determine what you qualify for</li>
              <li>Your energy account number, used to apply assistance to your bill</li>
              <li>Whether you agreed to receive text messages, and when</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">How we use it</h2>
            <p>
              We use this information to schedule and manage your appointment, to determine your
              eligibility for energy assistance, to pay assistance to your energy provider, and to meet the
              reporting rules of the programs that fund this assistance. We do not sell your information,
              and we do not use it for advertising.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Text messages and your mobile number</h2>
            <p>
              No mobile information will be shared with third parties or affiliates for marketing or
              promotional purposes. Text messaging originator opt-in data and consent are not shared with
              any third parties.
            </p>
            <p>
              We use a messaging provider to deliver texts and automated calls on our behalf. That provider
              may only use your number to deliver our messages. What we send, how often, and how to stop it
              are described in our{" "}
              <Link className="underline" to={smsProgram.termsPath}>
                Text Message Terms of Service
              </Link>
              .
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Who else sees your information</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Your energy provider, so that assistance can be applied to your account</li>
              <li>
                The state and federal agencies that fund energy assistance, for eligibility verification and
                program reporting
              </li>
              <li>
                Service providers who operate this website and our messaging on our behalf, limited to what
                they need to do that work
              </li>
              <li>Anyone else, only where the law requires it or where you have asked us to</li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">How long we keep it</h2>
            <p>
              We keep your application and appointment records for as long as the funding programs require,
              and then dispose of them securely.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Your choices</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>Reply STOP to any text to stop receiving texts. Your appointment is not affected.</li>
              <li>Leave the text message box unchecked when you book. Nothing else about your appointment changes.</li>
              <li>
                Ask staff at your appointment to correct your information, tell you what we hold about you,
                or stop contacting you a particular way.
              </li>
            </ul>
          </section>

          <section className="space-y-2">
            <h2 className="font-semibold text-slate-900">Contact us</h2>
            <p>
              {smsProgram.organizationName} Energy Assistance. If you are receiving texts from us, reply to
              any message we send you. Otherwise, ask staff at your appointment.
            </p>
          </section>

          <p className="pt-2">
            <Link className="underline" to="/booking">
              Back to appointment scheduling
            </Link>
          </p>
        </div>
      </main>

      <PublicFooter>Multi-Service Center &nbsp;·&nbsp; Energy Assistance</PublicFooter>
    </div>
  );
}
